/**
 * useUser Hook
 * 
 * Hook for accessing Auth0 user information, permissions, and authentication functions.
 * Extracts role-based permissions from Auth0 user metadata.
 * Supports multi-group access (group_ids array) with backward compatibility for single group_id.
 */

import { useAuth0 } from '@auth0/auth0-react';

/**
 * Hook for accessing user authentication state, profile, and permissions
 * 
 * @returns {Object} User authentication state, permissions, and functions
 */
export function useUser() {
  const { 
    user, 
    isAuthenticated, 
    isLoading, 
    logout, 
    getAccessTokenSilently 
  } = useAuth0();

  // Extract metadata from Auth0 user object
  // Metadata is stored in namespaced custom claim
  const metadata = user?.['https://dashboard.yapn.ai/app_metadata'] || user?.app_metadata || {};
  
  // Extract roles from Auth0 (can be in roles namespace or app_metadata)
  const rolesArray = user?.['https://dashboard.yapn.ai/roles'] || [];
  
  /**
   * User role and permissions
   * Check roles array first, then fall back to metadata.role
   */
  let role = metadata.role;
  
  // If no role in metadata, check roles array
  if (!role && Array.isArray(rolesArray)) {
    if (rolesArray.includes('yapn-super-admin') || rolesArray.includes('super_admin')) {
      role = 'super_admin';
    } else if (rolesArray.includes('yapn-admin') || rolesArray.includes('admin')) {
      role = 'admin';
    } else if (rolesArray.includes('yapn-group-admin') || rolesArray.includes('group_admin')) {
      role = 'group_admin';
    }
  }
  
  // Default to most restrictive if still no role
  if (!role) {
    role = 'staff';
  }
  
  // Support both new (group_ids array) and legacy (group_id string) formats
  // New format: group_ids: ["REDBIRD_GROUP", "FALAFEL_INC_GROUP"]
  // Legacy format: group_id: "REDBIRD_GROUP"
  const groupIds = metadata.group_ids || 
                   (metadata.group_id ? [metadata.group_id] : []);
  
  // Keep groupId for backward compatibility (first group or legacy single group)
  const groupId = metadata.group_id || (groupIds.length > 0 ? groupIds[0] : null);
  
  // Check if user has access to multiple groups
  const hasMultipleGroups = groupIds.length > 1;
  
  const isHubkomClient = metadata.isHubkomClient === true;

  const accessibleLocations = metadata.accessible_locations || [];
  const permissions = metadata.permissions || {
    view_calls: true,
    view_analytics: false,
    manage_billing: false,
    manage_users: false
  };

  /**
   * Logout function with proper returnTo URL
   */
  const handleLogout = () => {
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };

  /**
   * Permission checking functions
   */
  const canViewCalls = () => permissions.view_calls;
  const canViewAnalytics = () => permissions.view_analytics;
  const canManageBilling = () => permissions.manage_billing;
  const canManageUsers = () => permissions.manage_users;
  
  /**
   * Check if user can access a specific location
   */
  const canAccessLocation = (locationId) => {
    if (role === 'super_admin') return true;
    if (accessibleLocations === 'all') return true;
    if (Array.isArray(accessibleLocations)) {
      return accessibleLocations.includes(locationId);
    }
    return false;
  };

  /**
   * Check if user can access all locations in a group
   * Supports multi-group access
   */
  const canAccessGroup = (targetGroupId) => {
    if (role === 'super_admin') return true;
    // Check if targetGroupId is in user's accessible groups
    if (role === 'group_admin' && groupIds.includes(targetGroupId)) return true;
    return false;
  };

  /**
   * Get user's display name (fallback to email if no name)
   */
  const displayName = user?.name || user?.email || 'User';

  /**
   * Get user's profile picture URL
   */
  const profilePicture = user?.picture;

  /**
   * Get user's email
   */
  const email = user?.email;

  /**
   * Check if user has a profile picture
   */
  const hasProfilePicture = Boolean(user?.picture);

  return {
    // User data
    user,
    displayName,
    email,
    profilePicture,
    hasProfilePicture,
    
    // Role and permissions
    role,
    groupId,           // Legacy: single group (first group or backward compat)
    groupIds,          // NEW: Array of all accessible groups
    hasMultipleGroups, // NEW: Boolean indicating multi-group access
    isHubkomClient,
    accessibleLocations,
    permissions,
    
    // Permission checking functions
    canViewCalls,
    canViewAnalytics,
    canManageBilling,
    canManageUsers,
    canAccessLocation,
    canAccessGroup,
    
    // Authentication state
    isAuthenticated,
    isLoading,
    
    // Functions
    logout: handleLogout,
    getAccessTokenSilently,
  };
}

export default useUser;
